/**
 * NewMultiChoiceQuiz Component
 * 새 UI - 복수 선택형 문제 with inline choices
 * - 정오답 표시 없음, 답안 제출만
 * - #태그 스타일 선택 UI
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'
import { KatexText, SubmitButton, QuizMedia } from './shared'
import type { MultiChoiceQuizData, QuizSolveResult } from '../../types/legacyQuiz'
import { formatText } from '../../utils/quiz-parser'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface NewMultiChoiceQuizProps {
  quizId: string | number
  quiz: MultiChoiceQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
}

export function MultiChoiceQuizForDiary({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
}: NewMultiChoiceQuizProps) {
  const { t } = useTranslation('quiz')
  const [startTime] = useState(() => Date.now())

  const isSolved = solveResult !== null && solveResult !== undefined
  const answerCount = quiz.answerCount || 1

  // 보기 순서대로 정렬 (random 배열 사용)
  const sortedAnswers = useMemo(() => {
    if (quiz.random && Array.isArray(quiz.random) && quiz.random.length > 0) {
      return quiz.random.map(idx => quiz.choices[idx]).filter(Boolean)
    }
    return quiz.choices
  }, [quiz.choices, quiz.random])

  // 초기 선택 상태 계산
  const computedInitialKeys = useMemo(() => {
    if (initialAnswersProp && initialAnswersProp.length > 0) {
      return initialAnswersProp
    }
    return []
  }, [initialAnswersProp])

  const [selectedKeys, setSelectedKeys] = useState<string[]>(computedInitialKeys)
  const isInitialMount = useRef(true)

  // Sync from parent's initialAnswers — use shallow comparison to avoid
  // re-render loops (arrays compared by reference would loop otherwise).
  useEffect(() => {
    setSelectedKeys(prev => {
      if (prev.length === computedInitialKeys.length &&
          prev.every((v, i) => v === computedInitialKeys[i])) {
        return prev
      }
      return computedInitialKeys
    })
  }, [computedInitialKeys])

  // hideSubmitButton 모드에서 선택 변경 시 자동으로 onSolve 호출 (페이지 이동 시 상태 유지용)
  // Skip initial mount — parent already has the data from previousResponses.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (hideSubmitButton && !isSolved && !isViewSolved) {
      onSolve?.(quizId, selectedKeys, quiz.vars, Date.now() - startTime)
    }
  }, [selectedKeys, hideSubmitButton, isSolved, isViewSolved, onSolve, quizId, quiz.vars, startTime])

  const canSubmit = selectedKeys.length > 0

  // 보기 선택 - 복수 선택 모드
  const handleSelect = useCallback((key: string) => {
    if (isSolved || isViewSolved) return

    setSelectedKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key)
      }
      return [...prev, key]
    })
  }, [isSolved, isViewSolved])

  // 제출 버튼 클릭
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, selectedKeys, quiz.vars, elapsedTime)
  }, [selectedKeys, canSubmit, isSolved, startTime, onSolve, quizId, quiz.vars])

  return (
    <div className="space-y-6">
      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3"
      >
        <div className="inline-flex gap-2 items-center px-3 py-1.5 text-sm font-bold text-white bg-linear-to-r from-violet-500 to-purple-500 rounded-full shadow-2xs">
          <span>{t('types.newMultiChoice.label')}</span>
          {answerCount > 1 && (
            <span className="px-1.5 py-0.5 bg-white/20 rounded-xs text-[10px]">
              {t('types.newMultiChoice.selectCount', { count: answerCount })}
            </span>
          )}
        </div>
        <h3 className="text-xl font-bold leading-relaxed text-gray-900">
          <KatexText text={quiz.title} />
        </h3>
        {/* 문제 본문 */}
        {quiz.problem && (
          <div className="text-lg leading-relaxed text-gray-700">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
        {/* 문제 이미지/영상/Desmos */}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* 선택 안내 */}
      {!isSolved && !isViewSolved && answerCount > 1 && (
        <p className="text-base text-gray-500">
          {t('types.newMultiChoice.selectedProgress', { selected: selectedKeys.length, count: answerCount })}
        </p>
      )}

      {/* #태그 스타일 보기 */}
      <div
        role="listbox"
        aria-label={t('types.multiChoice.selectOption')}
        aria-multiselectable="true"
        className="flex flex-wrap gap-2"
      >
        {sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key)

          return (
            <motion.button
              key={answer.key}
              role="option"
              aria-selected={isSelected}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.03 }}
              onClick={() => handleSelect(answer.key)}
              disabled={isSolved || isViewSolved}
              className={cn(
                'px-3 py-1.5 rounded-lg transition-all text-base font-medium',
                // 기본 상태
                !isSelected && 'bg-gray-100 text-gray-600 hover:bg-violet-100 hover:text-violet-700',
                // 선택 상태
                isSelected && 'bg-violet-500 text-white shadow-2xs',
                // 제출 후 비활성화
                (isSolved || isViewSolved) && 'cursor-not-allowed opacity-70'
              )}
            >
              <span className="font-bold">#</span>
              <KatexText text={answer.answer} />
            </motion.button>
          )
        })}
      </div>

      {/* 제출 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? (selectedKeys.length === 0 ? t('types.newMultiChoice.selectOption') : t('types.newMultiChoice.submit'))}
        />
      )}

      {/* 제출 완료 표시 */}
      {isSolved && (
        <div className="p-4 text-center text-gray-600 bg-gray-100 rounded-xl">
          {t('types.newMultiChoice.submitted')}
        </div>
      )}
    </div>
  )
}
